/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Button, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Grid, GridItem } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import TextFormField from '../../components/form/TextFormField';
import DateFormField from '../form/DateFormField';
import GeoFormField from '../../components/form/GeoFormField';
import CheckboxFormField from '../../components/form/CheckBoxFormField';
import TextAreaFormField from '../../components/form/TextAreaFormField';
import MultipleFileFormField from '../form/MultipleFileFormField';
import moment from 'moment';
import c from '../../constant';
import * as Yup from 'yup';

const UPDATE_MUTATION = gql`
	mutation create(
		$geoLocation: String
		$isGeoManual: Boolean
		$geoManual: String
		$validTo: DateTime
		$validFrom: DateTime
		$name: String!
		$nameLatin: String
		$number: String
		$id: String!
		$managerName: String!
		$managerNameLatin: String!
		$files: JSON
	) {
		updateCompanyCertificateById(
			geoLocation: $geoLocation
			isGeoManual: $isGeoManual
			geoManual: $geoManual
			validTo: $validTo
			validFrom: $validFrom
			name: $name
			nameLatin: $nameLatin
			number: $number
			id: $id
			managerName: $managerName
			managerNameLatin: $managerNameLatin
			files: $files
		)
	}
`;

const QUERY_DATA = gql`
	query get($id: String!) {
		getCompanyCertificateById(id: $id) {
			id
			number
			name
			nameLatin
			managerName
			managerNameLatin
			validFrom
			validTo
			files
			streetNumber
			houseNumber
			geoManual
			isGeoManual
			province
			district
			commune
			village
		}
	}
`;

const signUpSchema = Yup.object().shape({
	number: Yup.string(),
	name: Yup.string().required('Required'),
	nameLatin: Yup.string(),
	managerName: Yup.string(),
	managerNameLatin: Yup.string(),
	isGeoManual: Yup.boolean(),
	geoManual: Yup.mixed().when('isGeoManual', {
		is: (isGeoManual) => isGeoManual === true,
		then: Yup.string().required('required'),
		otherwise: Yup.string(),
	}),
	geoLocation: Yup.object().nullable(),
	validFrom: Yup.string().nullable(),
	validTo: Yup.string().nullable(),
	files: Yup.array().of(
		Yup.object().shape({
			file: Yup.string(),
			tempFile: Yup.mixed().when('file', {
				is: (file) => (file ? true : false),
				then: Yup.string(),
				otherwise: Yup.string().required('required'),
			}),
		})
	),
});

function Create({ isOpen = false, onClose = () => null, callback = () => null, id }) {
	const toast = useToast();
	const { t } = useTranslation();
	const [isGeoManual, setIsGeoManual] = useState(false);
	const [updateMutation, { loading, error, data }] = useMutation(UPDATE_MUTATION);
	const [loadData, { data: dataData }] = useLazyQuery(QUERY_DATA);

	useEffect(() => {
		if (dataData?.getCompanyCertificateById?.isGeoManual) {
			setIsGeoManual(true);
		}
	}, [dataData]);

	useEffect(() => {
		if (id) {
			loadData({
				variables: {
					id,
				},
			});
		}
	}, [id]);

	useEffect(() => {
		if (data) {
			if (data?.updateCompanyCertificateById) {
				toast({
					title: t('Company certificate updated successfully'),
					status: 'success',
					isClosable: true,
					position: 'top-right',
				});
				onClose();
				callback('Company certificate updated successfully');
			}
		}
	}, [data]);

	useEffect(() => {
		if (error) {
			error.graphQLErrors.map(({ message }, i) =>
				toast({
					title: t(message),
					status: 'error',
					isClosable: true,
					position: 'top-right',
				})
			);
		}
	}, [error]);

	return (
		<>
			<Modal isOpen={isOpen} onClose={onClose} size="4xl">
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius} pb="2">
					<ModalHeader>{t('Edit Company Certificate')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						{dataData?.getCompanyCertificateById && (
							<Formik
								initialValues={{
									number: dataData?.getCompanyCertificateById?.number || '',
									name: dataData?.getCompanyCertificateById?.name || '',
									nameLatin: dataData?.getCompanyCertificateById?.nameLatin || '',
									managerName: dataData?.getCompanyCertificateById?.managerName || '',
									managerNameLatin: dataData?.getCompanyCertificateById?.managerNameLatin || '',
									geoLocation: {
										province: dataData?.getCompanyCertificateById?.province,
										district: dataData?.getCompanyCertificateById?.district,
										commune: dataData?.getCompanyCertificateById?.commune,
										village: dataData?.getCompanyCertificateById?.village,
										streetNumber: dataData?.getCompanyCertificateById?.streetNumber,
										houseNumber: dataData?.getCompanyCertificateById?.houseNumber,
									},
									isGeoManual: dataData?.getCompanyCertificateById?.isGeoManual || false,
									geoManual: dataData?.getCompanyCertificateById?.geoManual || '',
									validFrom: dataData?.getCompanyCertificateById?.validFrom
										? moment(dataData?.getCompanyCertificateById?.validFrom).toDate()
										: null,
									validTo: dataData?.getCompanyCertificateById?.validTo
										? moment(dataData?.getCompanyCertificateById?.validTo).toDate()
										: null,
									files: dataData?.getCompanyCertificateById?.files || [],
								}}
								validationSchema={signUpSchema}
								onSubmit={(values) => {
									const variables = {
										id: id,
										number: values?.number,
										name: values?.name,
										nameLatin: values?.nameLatin,
										managerName: values?.managerName,
										managerNameLatin: values?.managerNameLatin,
										geoLocation: values?.geoLocation ? JSON.stringify(values?.geoLocation) : null,
										isGeoManual: values?.isGeoManual,
										geoManual: values?.geoManual,
										validTo: values?.validTo,
										validFrom: values?.validFrom,
										files: values?.files,
									};
									updateMutation({ variables });
								}}
							>
								{(props) => {
									return (
										<Form>
											<Box mt="3">
												<Grid templateColumns="repeat(2, 1fr)" gap={6} w="100%">
													<GridItem w="100%">
														<TextFormField {...props} label="Number" name="number" />
														<TextFormField {...props} label="Name" name="name" required />
														<TextFormField {...props} label="Name Latin" name="nameLatin" />
														<TextFormField {...props} label="Branch Manager Name" name="managerName" />
														<TextFormField {...props} label="Branch Manager Name Latin" name="managerNameLatin" />
													</GridItem>
													<GridItem w="100%">
														{!isGeoManual && (
															<GeoFormField
																{...props}
																name="geoLocation"
																label="Address"
																buttomComponent={
																	<CheckboxFormField
																		{...props}
																		label="Input"
																		name="isGeoManual"
																		callbackValueChange={(e) => setIsGeoManual(e)}
																	/>
																}
															/>
														)}
														{isGeoManual && (
															<TextAreaFormField
																{...props}
																label="Address"
																name="geoManual"
																required={true}
																buttomComponent={
																	<CheckboxFormField
																		{...props}
																		label="Input"
																		name="isGeoManual"
																		callbackValueChange={(e) => setIsGeoManual(e)}
																	/>
																}
															/>
														)}
														<MultipleFileFormField name="files" label="Files" editing={true} />
													</GridItem>
												</Grid>
												<Grid templateColumns="repeat(2, 1fr)" gap={6} w="100%">
													<GridItem w="100%">
														<DateFormField
															name="validFrom"
															label="Valid From"
															initialDate={dataData?.getCompanyCertificateById?.validFrom}
														/>
													</GridItem>
													<GridItem w="100%">
														<DateFormField
															name="validTo"
															label="Valid To"
															initialDate={dataData?.getCompanyCertificateById?.validTo}
														/>
													</GridItem>
												</Grid>
												<Flex textAlign="right" mt="6" justifyContent="space-between" alignItems="center" float="right">
													<Button
														id="login-btn"
														variant="solid"
														size="md"
														h="42px"
														w="128px"
														bg="primary.dark"
														_hover={{ bg: 'primary.dark' }}
														color="white"
														type="submit"
														isLoading={loading}
														loadingText={t('Edit')}
														spinnerPlacement="start"
													>
														{t(`Edit`)}
													</Button>
												</Flex>
											</Box>
										</Form>
									);
								}}
							</Formik>
						)}
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

export default Create;
